import { AlphaFilter, type Container, Graphics, type Sprite } from 'pixi.js';
import { type ChargedAttack, PLUNGE } from '../../data/full/echassier';
import { chargeShare } from '../../sim/full/attacks/charged';
import type { SimState } from '../../sim/state';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, paintSheet } from '../paint';
import { mixColor } from '../palette';
import { type Look, type LookInput, fade } from '../player-look';
import { REFERENCE_RADIUS } from '../textures-looks';
import type { Textures } from '../textures';
import { add, hide, placeOutline, setTint } from '../util';
import { BUTTONS, ECHASSIER_PIECES, HANDS, HAT_AT, TAIL_SWAY, UNIT } from './echassier-paint';
import { SHADOW_ALPHA, drawStiltShadow, lightAngle } from './stilt-shadow';

type Parts = Readonly<Record<keyof typeof ECHASSIER_PIECES, Shape>>;

const EXTENT = 30;
const BOB_TICKS = 2;
const BEAT_SWELL = 0.03;
const DEEP = 0.32;
const UNLIT = 0x3b4170;
const POP_TICKS = 3;
const FULL = { white: 0.7, glow: 3, alpha: 0.8, pulse: 0.25 } as const;
const CALM = { swell: 0.3, sway: 0.4 } as const;
const PORTRAIT_SHADOW = 0.45;
// While he charges, his hat's ribbon fills clockwise from the front like a clock, and a tick on the
// brim lights for each bad vibe the shot will cross. In the drawing's units, around the hat.
const CLOCK = { ribbon: 7.4, tickFrom: 8.2, tickTo: 9.8, tick: 1.3, white: 0.6, pop: 0.8 } as const;

// Lit while he stands still, one more per bad vibe the shot will cross; then, while it flies, one goes
// out at each bad vibe it crosses.
function litButtons(state: SimState, input: LookInput, attack: ChargedAttack): number {
  const { player } = input;
  if (player.chargeTicks !== undefined) {
    const share = chargeShare(player, attack);
    return 1 + Math.floor((BUTTONS.length - 1) * share + 1e-9);
  }
  let newest = -1;
  let left = 0;
  for (const shot of state.projectiles) {
    if (shot.owner.kind === 'player' && shot.owner.playerId === player.id && shot.id > newest) {
      newest = shot.id;
      left = shot.pierceLeft + 1;
    }
  }
  return Math.min(BUTTONS.length, left);
}

function drawClock(
  g: Graphics,
  input: LookInput,
  attack: ChargedAttack,
  count: number,
  pop: number,
  beat: number,
): void {
  const { color } = input;
  const share = chargeShare(input.player, attack);
  const full = count === BUTTONS.length;
  const pale = mixColor(color, 0xffffff, CLOCK.white);
  g.clear();
  if (!full && share > 0) {
    g.moveTo(0, 0)
      .arc(0, 0, CLOCK.ribbon, 0, share * Math.PI * 2)
      .closePath()
      .fill({ color });
  }
  for (let index = 0; index < BUTTONS.length; index += 1) {
    const turn = (index / BUTTONS.length) * Math.PI * 2;
    const on = index < count;
    const swell = (index === count - 1 ? pop * CLOCK.pop : 0) + (full ? beat : 0);
    g.moveTo(Math.cos(turn) * CLOCK.tickFrom, Math.sin(turn) * CLOCK.tickFrom)
      .lineTo(Math.cos(turn) * CLOCK.tickTo, Math.sin(turn) * CLOCK.tickTo)
      .stroke({ width: CLOCK.tick * (1 + swell), color: on ? pale : UNLIT, cap: 'round' });
  }
}

function createStiltLook(
  bodies: Container,
  glowLayer: Container,
  textures: Textures,
  parts: Parts,
  attack: ChargedAttack,
): Look {
  const shadow = new Graphics();
  shadow.filters = [
    new AlphaFilter({ alpha: SHADOW_ALPHA, resolution: 'inherit', antialias: 'inherit' }),
  ];
  bodies.addChild(shadow);
  const halo = add(glowLayer, textures.halo);
  const outline = add(bodies, parts.silhouette);
  const tailLeft = add(bodies, parts.tailLeft);
  const tailRight = add(bodies, parts.tailRight);
  const back = add(bodies, parts.back);
  const shines = BUTTONS.map(() => add(bodies, parts.shine));
  const buttons = BUTTONS.map(() => add(bodies, parts.button));
  const torso = add(bodies, parts.torso);
  const hands = [add(bodies, parts.hand), add(bodies, parts.hand)] as const;
  const brim = add(bodies, parts.brim);
  const ribbon = add(bodies, parts.ribbon);
  const clock = new Graphics();
  bodies.addChild(clock);
  const crown = add(bodies, parts.crown);
  const sprites = [tailLeft, tailRight, back, torso, ...hands, brim, ribbon, crown, ...buttons];
  let lit = 0;
  let litAt = -Infinity;
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.visible = true;
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
  }

  return {
    hide() {
      hide(...sprites, ...shines, outline, halo);
      shadow.visible = false;
      clock.visible = false;
    },
    reset() {
      lit = 0;
      litAt = -Infinity;
    },
    place(input) {
      const { x, y, angle, scale, color, frame, state } = input;
      const { now, calm, tempo, light } = frame;
      const beats = (now - tempo.originTick) / tempo.ticksPerBeat;
      const swell = fade(input.sinceBeat, BOB_TICKS) * BEAT_SWELL * (calm ? CALM.swell : 1);
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale * (1 + swell);
      facing = angle;

      const sway = Math.sin(beats * Math.PI) * (calm ? CALM.sway : 1);
      const deep = mixColor(color, 0x000000, DEEP);
      put(tailLeft, 0, 0, sway * TAIL_SWAY);
      setTint(tailLeft, deep);
      put(tailRight, 0, 0, sway * TAIL_SWAY);
      setTint(tailRight, color);
      put(back, 0, 0);
      setTint(back, 0xffffff);
      put(torso, 0, 0);
      setTint(torso, color);
      placeOutline(outline, torso, parts.silhouette.texture, REFERENCE_RADIUS, frame);
      put(hands[0], HANDS.along + sway * HANDS.swing, -HANDS.across);
      put(hands[1], HANDS.along - sway * HANDS.swing, HANDS.across);
      put(brim, 0, 0);
      put(crown, 0, 0);

      const count = litButtons(state, input, attack);
      if (count > lit) {
        litAt = now;
      }
      lit = count;
      const charging = input.player.chargeTicks !== undefined;
      const full = charging && count === BUTTONS.length;
      const beat = calm ? 0 : fade(input.sinceBeat, BOB_TICKS * 2) * FULL.pulse;
      clock.visible = charging && !frame.portrait;
      if (clock.visible) {
        drawClock(clock, input, attack, count, calm ? 0 : fade(now - litAt, POP_TICKS), beat);
        clock.position.set(x + HAT_AT * cos * UNIT * size, y + HAT_AT * sin * UNIT * size);
        clock.rotation = facing;
        clock.scale.set(UNIT * size);
      }
      BUTTONS.forEach((spot, index) => {
        const button = buttons[index];
        const shine = shines[index];
        if (button === undefined || shine === undefined) {
          return;
        }
        put(button, spot.along, spot.across);
        const on = index < count;
        setTint(button, on ? mixColor(color, 0xffffff, 0.35) : UNLIT);
        shine.visible = on;
        if (on) {
          const pop = index === count - 1 && !calm ? fade(now - litAt, POP_TICKS) : 0;
          put(shine, spot.along, spot.across);
          shine.scale.set(size * (1 + pop * 0.6 + (full ? beat : 0)));
          setTint(shine, color);
          shine.alpha = 0.85;
        }
      });

      put(ribbon, 0, 0);
      setTint(ribbon, full ? mixColor(color, 0xffffff, FULL.white) : charging ? UNLIT : color);
      halo.visible = full;
      if (full) {
        const hatX = x + HAT_AT * cos * UNIT * size;
        const hatY = y + HAT_AT * sin * UNIT * size;
        halo.position.set(hatX, hatY);
        halo.scale.set(((input.player.radius * FULL.glow) / textures.halo.radius) * (1 + beat));
        setTint(halo, color);
        halo.alpha = FULL.alpha * light.haloAlpha;
      }

      shadow.visible = frame.portrait;
      if (frame.portrait) {
        const light = lightAngle(frame.fraction);
        const spread = Math.cos(light + Math.PI / 2) * 6 * scale;
        const across = Math.sin(light + Math.PI / 2) * 6 * scale;
        shadow.clear();
        drawStiltShadow(shadow, {
          x,
          y,
          scale,
          facing: angle,
          lightX: Math.cos(light),
          lightY: Math.sin(light),
          feet: [
            { x: x - spread, y: y - across, lift: 0 },
            { x: x + spread, y: y + across, lift: 0 },
          ],
          beats,
          calm,
          length: PORTRAIT_SHADOW,
        });
      }
      return 0;
    },
  };
}

export const ECHASSIER_DRAWING: ClassDrawing = {
  classId: 'echassier',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(ECHASSIER_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      shadow: shapes.shadow,
      source,
      create: (bodies, glow, textures) => createStiltLook(bodies, glow, textures, shapes, PLUNGE),
    };
  },
};
